/* eslint-disable react/jsx-key */

import React, { useEffect, useMemo } from 'react';

import { SortByAlpha, ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { Pagination, Select, MenuItem, LinearProgress } from '@mui/material';
import {
  useTable,
  Column,
  useResizeColumns,
  useFlexLayout,
  useSortBy,
  usePagination,
  useFilters,
  TableOptions,
} from 'react-table';

import { Spinner } from 'components/Spinner';
import { useUrlPagination } from 'hooks';
import {
  DefaultTableGlobalFilterOptions,
  AdminCandidateTableGlobalFilterOptions,
  UserTableGlobalFilterOptions,
} from 'types';

import { TableWrapper, MessageRow } from './styleComponents';
import { FilterBasic } from './TableFilters/FilterBasic';
export type TableControl = {
  key: string;
  text: string;
  icon: JSX.Element;
  disabled: boolean;
  click: () => void;
};
export type TableControls = {
  ml: number;
  selectAll: (props: { action: boolean; entities: Array<any> }) => void;
  controls: Array<TableControl>;
  selected: Set<number>;
};

export type StaticTableProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  addButton?: JSX.Element;
  useTableOptions: TableOptions<T>;
  loading: boolean;
  showPagination?: boolean;
  pageSizeOptions?: Array<number>;
  className?: string;
  globalFiltersApplied?: boolean;
  handleGlobalFilterClear?: () => void;
  count?: number;
  tableControls?: TableControls;
  setFilter?: React.Dispatch<
    React.SetStateAction<
      | AdminCandidateTableGlobalFilterOptions
      | UserTableGlobalFilterOptions
      | DefaultTableGlobalFilterOptions
    >
  >;
};

export const StaticTable = <
  T extends Record<string, unknown> = Record<string, unknown>,
>(
  props: StaticTableProps<T>,
) => {
  const {
    loading,
    useTableOptions,
    showPagination = true,
    pageSizeOptions = [10, 20, 30, 40, 50],
    className,
    globalFiltersApplied,
    handleGlobalFilterClear,
    count,
    tableControls,
    setFilter,
  } = props;

  const {
    controlledPageIndex,
    controlledRows,
    updateControlledPageIndex,
    updateControlledRows,
  } = useUrlPagination();
  const defaultColumn: Partial<Column<T>> = useMemo(
    () => ({
      // Default Filter UI
      Filter: FilterBasic,
    }),
    [],
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    setAllFilters,
    gotoPage,
    setPageSize,
    state: { pageSize, pageIndex, filters },
  } = useTable(
    {
      defaultColumn,
      ...useTableOptions,
    },

    useFlexLayout,
    useResizeColumns,
    useFilters,
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    if (pageIndex !== controlledPageIndex - 1) {
      gotoPage(controlledPageIndex - 1);
    }
    if (pageSize !== controlledRows) {
      setPageSize(controlledRows);
    }
  }, [
    pageSize,
    controlledPageIndex,
    controlledRows,
    pageIndex,
    gotoPage,
    setPageSize,
  ]);
  return (
    <TableWrapper className={className}>
      <div className='table' {...getTableProps()}>
        <div className='thead'>
          {headerGroups.map((headerGroup) => (
            <div className='tr' {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <div
                  {...column.getHeaderProps(
                    column.getSortByToggleProps({
                      className: `th ${column.classNameHeader ?? ''}`,
                    }),
                  )}
                >
                  <div className='th wrapper'>
                    {column.render('Header')}
                    {column.canSort &&
                      (column.isSorted ? (
                        column.isSortedDesc ? (
                          <ArrowDropDown />
                        ) : (
                          <ArrowDropUp />
                        )
                      ) : (
                        <SortByAlpha />
                      ))}
                    {column.canResize && (
                      <div
                        {...column.getResizerProps()}
                        className={`resizer ${
                          column.isResizing ? 'isResizing' : ''
                        }`}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {headerGroups.map((headerGroup) => (
            <div className='tr filter' {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => {
                return (
                  <div
                    {...column.getHeaderProps({
                      className: `th filter${column.classNameHeader ?? ''}`,
                    })}
                  >
                    {/* TODO: Enable the abiltiy to remove filtering */}
                    {column.canFilter ? column.render('Filter') : null}
                  </div>
                );
              })}
            </div>
          ))}
          {loading && <LinearProgress sx={{ width: '100%' }} />}
          {(globalFiltersApplied || filters.length > 0) && (
            <MessageRow>
              Filters Are Currently Applied. &nbsp;
              <u
                onClick={() => {
                  setAllFilters([]);
                  if (handleGlobalFilterClear) {
                    handleGlobalFilterClear();
                  }
                }}
              >
                Clear Filters
              </u>
            </MessageRow>
          )}
        </div>
        {loading && <Spinner />}
        <div className='tbody' {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <div
                className={
                  tableControls?.selected.has(row.original.id as number)
                    ? 'tr checked'
                    : 'tr'
                }
                style={{ cursor: 'pointer' }}
                {...row.getRowProps()}
              >
                {row.cells.map((cell) => {
                  return (
                    <div
                      {...cell.getCellProps({
                        className: `td ${cell.column.className}`,
                      })}
                    >
                      {cell.render('Cell')}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      {showPagination && (
        <div className='pagination'>
          <div>
            <span>
              {pageIndex * pageSize}-
              {(pageIndex + 1) * pageSize > (count ? count : rows.length)
                ? rows.length
                : (pageIndex + 1) * pageSize}{' '}
              from {count ? count : rows.length}
            </span>

            <Pagination
              count={pageCount}
              page={controlledPageIndex}
              onChange={(_, page_number) => {
                updateControlledPageIndex(page_number);
                if (typeof setFilter === 'function') {
                  setFilter((prev) => ({ ...prev, page_number }));
                }
              }}
            />

            <div className='pageSelect'>
              <span>Rows per page</span>
              <Select
                disabled={rows.length < pageSize}
                value={controlledRows}
                variant='standard'
                onChange={({ target: { value } }) => {
                  const limit = Number(value);
                  updateControlledRows(limit);
                  if (typeof setFilter === 'function') {
                    setFilter((prev) => ({ ...prev, limit }));
                  }
                }}
              >
                {pageSizeOptions.map((pageSizeOption) => (
                  <MenuItem key={pageSizeOption} value={pageSizeOption}>
                    {pageSizeOption}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </div>
      )}
    </TableWrapper>
  );
};
